import React from 'react';
import { ChevronRight, Download } from 'lucide-react';
import { AppModal, AppModalHeader } from '../../../components/AppModal';
import { LoadingSpinner } from '../../../components/LoadingSpinner';

type PreviewRow =
  | { kind: 'title'; oz: string; name: string; level: number }
  | { kind: 'text'; oz: string; name: string; description: string }
  | {
      kind: 'item';
      oz: string;
      name: string;
      description: string;
      quantity: number;
      unit: string;
      is_eventual: boolean;
    };

type Preview = {
  filename: string;
  source: 'original' | 'generated';
  dp: string;
  rows: PreviewRow[];
};

interface Props {
  open: boolean;
  onClose: () => void;
  /** `/api/ausschreibungen/:id` */
  base: string;
}

const qtyFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 });

/** Vorschau der X83, die an die Großhändler geht: dieselbe Datei wie der Anhang, ohne Preise. */
export const TenderGaebPreviewModal: React.FC<Props> = ({ open, onClose, base }) => {
  const [data, setData] = React.useState<Preview | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [expanded, setExpanded] = React.useState<Set<number>>(() => new Set());

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    fetch(`${base}/supplier-requests/attachment?preview=1`, { credentials: 'include' })
      .then(async (r) => {
        const body = (await r.json().catch(() => ({}))) as Preview & { error?: string };
        if (cancelled) return;
        if (!r.ok) throw new Error(body.error || 'Vorschau konnte nicht geladen werden.');
        setData(body);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [open, base]);

  const itemCount = data?.rows.filter((r) => r.kind === 'item').length ?? 0;

  const toggle = (i: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  return (
    <AppModal open={open} onClose={onClose} overlayClassName="z-50" panelClassName="md:max-w-4xl md:w-full">
      <AppModalHeader title={data ? `Vorschau ${data.filename}` : 'Vorschau GAEB'} onClose={onClose} />
      <div className="flex min-h-0 flex-1 flex-col">
        {data && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-app-border px-5 py-2.5 text-sm text-app-text-subtle">
            <span>
              GAEB {data.dp} · {itemCount} {itemCount === 1 ? 'Position' : 'Positionen'} · ohne Preise
            </span>
            <span>{data.source === 'original' ? 'Original-Datei des Auftraggebers' : 'Aus dem LV erzeugt'}</span>
            <a
              href={`${base}/supplier-requests/attachment`}
              className="ml-auto inline-flex items-center gap-1 font-medium text-pipedrive-blue hover:underline"
            >
              <Download className="h-4 w-4" />
              Herunterladen
            </a>
          </div>
        )}
        <div className="app-scrollbar min-h-0 flex-1 overflow-y-auto md:max-h-[70vh]">
          {error ? (
            <p className="px-5 py-6 text-sm text-red-700">{error}</p>
          ) : !data ? (
            <div className="flex justify-center py-10">
              <LoadingSpinner size="sm" />
            </div>
          ) : data.rows.length === 0 ? (
            <p className="px-5 py-6 text-sm text-app-text-subtle">Die Datei enthält keine Positionen.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-app-surface text-left text-xs font-medium text-app-text-subtle shadow-[0_1px_0_var(--color-app-border)]">
                <tr>
                  <th className="w-28 px-5 py-2 font-medium">OZ</th>
                  <th className="px-2 py-2 font-medium">Leistung</th>
                  <th className="w-24 px-2 py-2 text-right font-medium">Menge</th>
                  <th className="w-20 px-5 py-2 font-medium">Einheit</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => {
                  if (r.kind === 'title') {
                    return (
                      <tr key={i} className="border-t border-app-border bg-app-surface-muted/50">
                        <td className="px-5 py-2 font-semibold tabular-nums text-app-text">{r.oz}</td>
                        <td colSpan={3} className="px-2 py-2 font-semibold text-app-text">
                          {r.name}
                        </td>
                      </tr>
                    );
                  }
                  if (r.kind === 'text') {
                    return (
                      <tr key={i} className="border-t border-app-border">
                        <td />
                        <td colSpan={3} className="whitespace-pre-line px-2 py-2 text-app-text-subtle">
                          {r.description || r.name}
                        </td>
                      </tr>
                    );
                  }
                  const open = expanded.has(i);
                  const hasLong = Boolean(r.description?.trim() && r.description.trim() !== r.name.trim());
                  return (
                    <React.Fragment key={i}>
                      <tr className="border-t border-app-border align-top">
                        <td className="px-5 py-2 tabular-nums text-app-text-muted">{r.oz}</td>
                        <td className="px-2 py-2 text-app-text">
                          {hasLong ? (
                            <button
                              type="button"
                              onClick={() => toggle(i)}
                              className="inline-flex items-start gap-1 text-left hover:text-pipedrive-blue"
                              aria-expanded={open}
                            >
                              <ChevronRight
                                className={`mt-0.5 h-4 w-4 shrink-0 text-app-text-subtle transition-transform ${open ? 'rotate-90' : ''}`}
                              />
                              <span>{r.name}</span>
                            </button>
                          ) : (
                            <span className="pl-5">{r.name}</span>
                          )}
                          {r.is_eventual && (
                            <span className="ml-2 rounded bg-amber-500/10 px-1.5 py-0.5 text-xs text-amber-700">
                              Bedarf
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums text-app-text">{qtyFormat.format(r.quantity)}</td>
                        <td className="px-5 py-2 text-app-text-muted">{r.unit}</td>
                      </tr>
                      {open && hasLong && (
                        <tr>
                          <td />
                          <td colSpan={3} className="whitespace-pre-line px-2 pb-3 pl-7 text-app-text-subtle">
                            {r.description}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppModal>
  );
};
